import { Dispatch, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import {
  Dropzone,
  DropzoneProps,
  FileRejection,
  FileWithPath,
} from '@mantine/dropzone';
import clsx from 'clsx';

import { fileSizeFormatter } from '../../utils/fileSizeFormatter';
import getFileExtensions from '../../utils/getFileExtensions';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Popover } from '../popover/Popover';

import {
  FileSelection,
  FileSelectionAction,
  FileSelectionState,
} from './fileUploadState';
import { FileSection } from './UploadFileSection';
import { FileData, isFileSelection } from './uploadTypes';

import '@mantine/dropzone/styles.css';
import styles from './upload.module.scss';

type TotalSizeResult = { accepted: FileWithPath[]; rejected: FileRejection[] };

export function splitFilesByTotalSize(
  filesToUpload: FileWithPath[],
  currentSelections: FileSelectionState,
  maxTotalSize: number | undefined,
  formatMessage: (totalSize: number) => string
): TotalSizeResult {
  if (!maxTotalSize) {
    return { accepted: filesToUpload, rejected: [] };
  }

  const currentSize = currentSelections.reduce(
    (sum, file) => sum + file.file.size,
    0
  );

  let runningSize = 0;
  const accepted: FileWithPath[] = [];
  const rejected: FileRejection[] = [];

  for (const file of filesToUpload) {
    const projectedSize = currentSize + runningSize + file.size;
    if (projectedSize > maxTotalSize) {
      rejected.push({
        file,
        errors: [
          {
            code: 'total-size-exceeded',
            message: formatMessage(maxTotalSize),
          },
        ],
      });
      continue;
    }

    runningSize += file.size;
    accepted.push(file);
  }

  return { accepted, rejected };
}

export type UploadProps = {
  title?: string;
  /** material icon name to be shown on idle state. Defaults to "draft" */
  icon?: string;
  maxFileSize?: number;
  maxFiles?: number;
  /** Optional maximum total size in bytes across all files */
  maxTotalSize?: number;
  fileState: FileSelectionState;
  initialInvalidFiles?: FileRejection[];
  dispatch: Dispatch<FileSelectionAction>;
  accept?: DropzoneProps['accept'];
  subtitle?: string;
  uploadFile?: (file: FileSelection) => Promise<void>;
  fileNameMetadataExtractionConfig?: FileUploadMetaDataExtractionDto;
};

type InfoIconContentProps = { accept: DropzoneProps['accept'] };

function InfoIconContent({ accept }: InfoIconContentProps) {
  return (
    <>
      {getFileExtensions(accept)
        .map((ext) => `.${ext}`)
        .join(', ')}
    </>
  );
}

export function Upload({
  dispatch,
  fileNameMetadataExtractionConfig,
  fileState,
  initialInvalidFiles = [],
  uploadFile,
  maxFiles,
  ...props
}: UploadProps) {
  const { t } = useTranslation();
  const {
    accept,
    icon = 'upload_file',
    maxFileSize = 50 * 1024 ** 2, // 50 MB (default)
    maxTotalSize = 250 * 1024 ** 2, // 250 MB (default)
    subtitle = t('app.upload.subtitle', { count: maxFiles ?? 0 }),
    title = t('app.upload.title', { count: maxFiles ?? 0 }),
  } = props;

  const [invalidFiles, setInvalidFiles] =
    useState<FileRejection[]>(initialInvalidFiles);

  const considerInvalidFiles = useCallback(
    (invalidFiles: FileRejection[], currentCount: number): boolean => {
      if (
        invalidFiles.length + currentCount <= (maxFiles ?? 0) &&
        invalidFiles.every(
          (entry) =>
            entry.errors.length === 1 &&
            entry.errors[0].code === 'too-many-files'
        )
      ) {
        dispatch({
          type: 'addFileSelections',
          payload: invalidFiles.map((entry) => entry.file),
        });
        setInvalidFiles([]);

        return true;
      }

      return false;
    },
    [dispatch, maxFiles]
  );

  const onRemoveFile = useCallback(
    (fileData: FileData) => {
      if (isFileSelection(fileData)) {
        dispatch({ type: 'removeFileSelections', payload: [fileData.id] });

        considerInvalidFiles(invalidFiles, fileState.length - 1);
      } else {
        const remainingInvalidFiles = invalidFiles.filter(
          ({ file: f }) => f !== fileData.file
        );

        if (considerInvalidFiles(remainingInvalidFiles, fileState.length)) {
          return;
        }

        setInvalidFiles(remainingInvalidFiles);
      }
    },
    [considerInvalidFiles, dispatch, fileState.length, invalidFiles]
  );

  const isDropzoneDisabled =
    fileState.length >= (maxFiles ?? Number.MAX_SAFE_INTEGER); // No default max files

  // Dropzone maxFiles applies per drop; pass remaining capacity so totals stay within maxFiles.
  const remainingFileSlots =
    maxFiles == null ? undefined : Math.max(0, maxFiles - fileState.length);

  const buildTotalSizeRejections = useCallback(
    (filesToUpload: FileWithPath[], currentSelections: FileSelectionState) =>
      splitFilesByTotalSize(
        filesToUpload,
        currentSelections,
        maxTotalSize,
        (maxTotalSizeValue) =>
          t('app.upload.maxTotalSizeExceeded', '', {
            maxTotalSize: fileSizeFormatter(maxTotalSizeValue),
          })
      ),
    [maxTotalSize, t]
  );

  const onFileDrop = (filesToUpload: FileWithPath[]) => {
    const { accepted, rejected } = buildTotalSizeRejections(
      filesToUpload,
      fileState
    );
    if (rejected.length > 0) {
      setInvalidFiles((prev) => [...prev, ...rejected]);
    }

    // If metadata extraction is not enabled in poco, directly dispatch the files
    if (!fileNameMetadataExtractionConfig) {
      return dispatch({
        type: 'addFileSelections',
        payload: accepted,
      });
    }

    const { regex, enabled } = fileNameMetadataExtractionConfig;

    if (!enabled || !regex) {
      // If extraction is not enabled or regex is missing, fallback to default dispatch
      return dispatch({
        type: 'addFileSelections',
        payload: accepted,
      });
    }

    // Process files with metadata extraction logic
    const payload = accepted.map((file) => {
      const { name } = file;
      const match = name.match(regex);

      // Extract metadata if it exists, otherwise default to an empty string
      const metaData = match?.groups?.value || '';

      return { file, metaData };
    });

    dispatch({
      type: 'addFileSelectionsWithMetadata',
      payload,
    });
  };

  const hasUploadsStarted = fileState.some((f) => f.uploadState !== 'initial');

  const successCount = fileState.reduce((count, current) => {
    if (current.uploadState === 'isSuccess') {
      return count + 1;
    }
    return count;
  }, 0);
  const problemCount = fileState.reduce((count, current) => {
    if (current.uploadState === 'isError') {
      return count + 1;
    }
    return count;
  }, 0);

  return (
    <div data-testid="upload">
      {hasUploadsStarted ? (
        <div>
          <div className={styles['upload-info']}>
            <GoogleFontIcon name="info" solid />
            {t('app.upload.info.progress', '', {
              count: successCount,
              total: fileState.length,
            }) +
              (problemCount
                ? ` ${t('app.upload.info.problemCount', '', { count: problemCount, total: fileState.length })}`
                : '')}
          </div>
          <div className={styles['file-sections']}>
            <FileSection
              files={fileState}
              onRemoveFile={onRemoveFile}
              showUploadState
              title={t('app.upload.uploadedFiles')}
              uploadFile={uploadFile}
            />
          </div>
        </div>
      ) : (
        <>
          <Dropzone
            data-testid="upload-dropzone"
            {...props}
            maxFiles={remainingFileSlots}
            disabled={isDropzoneDisabled}
            onDrop={onFileDrop}
            onReject={(files) => setInvalidFiles((prev) => [...prev, ...files])}
            maxSize={maxFileSize}
            classNames={{
              root: styles['dropzone'],
              inner: styles['dropzone-inner'],
            }}
            useFsAccessApi={false} // Spl case needed for uploading .xlsm type files, ref [PF-3974]
          >
            <Dropzone.Accept>
              <GoogleFontIcon
                name="upload"
                className={styles['dropzone-icon']}
              />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <GoogleFontIcon
                name="report"
                className={styles['dropzone-icon']}
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <GoogleFontIcon
                name={icon}
                className={clsx(
                  styles['dropzone-icon'],
                  isDropzoneDisabled && styles['dropzone-icon-disabled']
                )}
              />
            </Dropzone.Idle>

            <p>
              {title}
              <span
                className={clsx(styles['dropzone-subtitle'], {
                  [styles['dropzone-subtitle-disabled']]: isDropzoneDisabled,
                })}
              >
                {subtitle}
              </span>
            </p>
          </Dropzone>
          <div className={styles['footer']}>
            {accept != null && (
              <div>
                <Popover content={<InfoIconContent accept={accept} />}>
                  <GoogleFontIcon
                    name="info"
                    className={styles['permitted-formats-icon']}
                  />
                </Popover>
                {t('app.upload.permittedFormats')}
              </div>
            )}
            {maxFileSize != null &&
              (maxTotalSize == null || maxTotalSize !== maxFileSize) && (
                <div>
                  {t('app.upload.maxFileSize')}:{' '}
                  {fileSizeFormatter(maxFileSize)}
                </div>
              )}
            {maxTotalSize != null && (
              <div>
                {t('app.upload.maxTotalSize')}:{' '}
                {fileSizeFormatter(maxTotalSize)}
              </div>
            )}
            {maxFiles != null && (
              <div>
                {t('app.upload.maxFileCount')}: {maxFiles}
              </div>
            )}
          </div>
          <div className={styles['file-sections']}>
            <FileSection
              files={invalidFiles}
              onRemoveFile={onRemoveFile}
              showUploadState={false}
              title={t('app.upload.rejected')}
            />
            <FileSection
              files={fileState}
              onRemoveFile={onRemoveFile}
              showUploadState={false}
              title={t('app.upload.addedToUpload')}
              uploadFile={uploadFile}
            />
          </div>
        </>
      )}
    </div>
  );
}
